import { Typography, colors } from '@leapeasy/ui-kit';
import { Box } from '@mui/material';

import ReportingPageImg from 'assets/guide/ReportingPage.png';
import BuildingsTabImg from 'assets/guide/BuildingsTab.png';
import BuildingsReportTabImg from 'assets/guide/BuildingsReportTab.png';
import TreeviewImg from 'assets/guide/Treeview.png';
import TreeviewEditImg from 'assets/guide/TreeViewEdit.png';
import AdminNewInviteImg from 'assets/guide/AdminNewInvite.png';
import AdminNewInviteTypeImg from 'assets/guide/AdminNewInviteType.png';
import AdminLLInviteImg from 'assets/guide/AdminLLInvite.png';
import ReInviteUserImg from 'assets/guide/ReInviteUser.png';
import { isFlaggedCancellationsUiVisible } from 'utils/flaggedCancellationUi';
import {
  formatGuideList,
  getPropertyMenuGuideItems,
  getSupportingRoleGuideLabels,
  PORTAL_GUIDE_APPLICATION_PROGRAMS,
  PORTAL_GUIDE_POLICY_ISSUED_STAGE,
} from 'utils/portalGuideUi';
import { GuideScreenshot } from './GuideScreenshot';

const supportingRoleGuideLabels = getSupportingRoleGuideLabels();

export const DashboardPage = () => {
  return (
    <Box marginLeft="30px">
      <Typography
        variant="h2"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Dashboard
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        The Dashboard is an interactive reporting page that lets you view portfolio performance
        across your delegated properties. Open it from the main navigation at <b>/dashboard</b>.
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        KPI cards at the top summarize user counts, property counts, and unit totals. Click a card
        to drill into the related list — for example, Total Operators opens the Operator user list,
        and Total Properties opens the property user list or property list depending on your role.
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Below the KPI cards, charts show application volume, policy issuance, coverage amounts,
        cancellations, declines, rent supported by Leap, and other portfolio metrics scoped to your
        role and delegations.
      </Typography>
      <GuideScreenshot src={ReportingPageImg} alt="Dashboard reporting page" />
    </Box>
  );
};

export const PropertyPage = () => {
  const propertyMenuItems = formatGuideList(getPropertyMenuGuideItems());

  return (
    <Box marginLeft="30px">
      <Typography
        variant="h2"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Portfolio
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        The <b>Portfolio</b> section in the main menu groups everything related to your delegated
        properties: {propertyMenuItems}.
      </Typography>
      <Typography
        variant="h3"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Properties
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Open <b>Portfolio → Properties</b> to see properties delegated to you. Each row shows the
        Leap property record name, property type, phone number, address, contact email, total units,
        and student property status.
      </Typography>
      <GuideScreenshot src={BuildingsTabImg} alt="Properties list" />
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Double-click a property row to open its detail view. The detail page includes <b>Reports</b>
        , <b>Policies</b>, <b>Applications</b>, <b>Claims</b>, and <b>Details</b> tabs with data
        scoped to the selected property.
      </Typography>
      <GuideScreenshot src={BuildingsReportTabImg} alt="Property detail reports tab" />
      <Typography
        variant="h3"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Applications
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        <b>Portfolio → Applications</b> lists all applications for your delegated properties.
        Program types include {PORTAL_GUIDE_APPLICATION_PROGRAMS}. Search, filter by column, print,
        or download the list. Double-click a row to open application details. Policy Issued
        applications include quick actions to file a claim or download insurance documents.
      </Typography>
      <Typography
        variant="h3"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Policies
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        <b>Portfolio → Policies</b> lists issued policies at stage{' '}
        <b>{PORTAL_GUIDE_POLICY_ISSUED_STAGE}</b> (shown in the portal as Policy Issued) for your
        delegated properties.
      </Typography>
      {isFlaggedCancellationsUiVisible() && (
        <>
          <Typography
            variant="h3"
            style={{
              color: colors.purple[900],
              fontWeight: '500',
              paddingBottom: '10px',
              paddingTop: '10px',
            }}
          >
            Flagged Cancellations
          </Typography>
          <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
            <b>Portfolio → Flagged Cancellations</b> surfaces policies that need attention. A badge
            on the Portfolio menu appears when flagged items exist for your account.
          </Typography>
        </>
      )}
      <Typography
        variant="h3"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Claims
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        <b>Portfolio → Claims</b> lists all claims attached to your delegated properties.
      </Typography>
    </Box>
  );
};

export const UserPage = () => {
  return (
    <Box marginLeft="30px">
      <Typography
        variant="h2"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Users
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        The <b>Users</b> section in the main menu provides access to your delegation hierarchy and
        user lists by role: Tree View, Operator, Full Portfolio, Multi-Site, and Property Manager.
      </Typography>
      <Typography
        variant="h3"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Tree View
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        <b>Users → Tree View</b> shows the delegation hierarchy. Authorized users can review the
        tree structure and edit delegations between roles.
      </Typography>
      <GuideScreenshot src={TreeviewImg} alt="User tree view" />
      <GuideScreenshot src={TreeviewEditImg} alt="Edit user tree view" />
      <Typography
        variant="h3"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Operator
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Admin users can view all Operator users on the portal from <b>Users → Operator</b>.
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Double-click a row to open the operator detail page with reports, applications, properties,
        claims, and profile details.
      </Typography>
      <Typography
        variant="h3"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Full Portfolio, Multi-Site, Property Manager
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Each tab lists users at that role level who have been delegated to you. Higher-level users
        see more users than lower-level users — for example, an Operator user can see Full
        Portfolio, Multi-Site, and Property Manager users; a Full Portfolio user cannot see Operator
        users but can see Multi-Site and Property Manager users below them; a Multi-Site user can
        see Property Manager users; and a Property Manager user cannot see users above their level.
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Authorized higher-level users can update property delegations for users below them and
        remove users when permitted.
      </Typography>
    </Box>
  );
};

export const InviteUserPage = () => {
  return (
    <Box marginLeft="30px">
      <Typography
        variant="h2"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Invite New User
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Admin users can invite supporting users ({supportingRoleGuideLabels}) on behalf of Operator
        users from <b>Add Users → Invite New User</b>.
      </Typography>
      <GuideScreenshot src={AdminNewInviteImg} alt="Invite new user form" />
      <GuideScreenshot src={AdminNewInviteTypeImg} alt="Select invite user type" />
    </Box>
  );
};

export const LandlordInvitePage = () => {
  return (
    <Box marginLeft="30px">
      <Typography
        variant="h2"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Operator Invite
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        Admin users can invite new Operator users from <b>Add Users → Operator Invite</b>.
      </Typography>
      <GuideScreenshot src={AdminLLInviteImg} alt="Operator invite form" />
      <GuideScreenshot src={ReInviteUserImg} alt="Operator invite confirmation" />
    </Box>
  );
};

export const EmailPage = () => {
  return (
    <Box marginLeft="30px">
      <Typography
        variant="h2"
        style={{
          color: colors.purple[900],
          fontWeight: '500',
          paddingBottom: '10px',
          paddingTop: '10px',
        }}
      >
        Invitations
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        <b>Add Users → Invitations</b> shows invitations you have sent or received. From here you
        can review invitation status, open invitation details, and resend invites when needed.
      </Typography>
      <Typography variant="body2" style={{ color: colors.purple[900], fontWeight: '500' }}>
        <b>Add Users → Notification</b> lists delegation and system notifications. Open a row to
        review received or outgoing notification details.
      </Typography>
      <GuideScreenshot src={ReInviteUserImg} alt="Invitations list" />
    </Box>
  );
};
